import React, { useState, useEffect, useCallback } from 'react'
import styled from 'styled-components'
import { Select, Input, Switch, Button, Space, Tooltip } from 'antd'
import { 
  IconSearch, 
  IconFilter,
  IconPlus,
  IconRefresh,
  IconDownload,
  IconChevronUp,
  IconChevronDown,
  IconX
} from '@tabler/icons-react'

const FilterContainer = styled.div<{ $fullWidth?: boolean }>`
  background: rgba(255, 255, 255, 0.95);
  backdrop-filter: blur(10px);
  border: 1px solid rgba(0, 0, 0, 0.1);
  border-radius: ${props => props.$fullWidth ? '0' : '8px'};
  padding: 12px ${props => props.$fullWidth ? '50px' : '12px'};
  margin: ${props => props.$fullWidth ? '0 -50px 16px' : '0 0 16px 0'};
  position: relative;
  width: ${props => props.$fullWidth ? 'calc(100% + 100px)' : '100%'};
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  
  @media (max-width: 768px) {
    padding: 12px ${props => props.$fullWidth ? '5px' : '12px'};
    margin: ${props => props.$fullWidth ? '0 -5px 16px' : '0 0 16px 0'};
    width: ${props => props.$fullWidth ? 'calc(100% + 10px)' : '100%'};
  }
`

const FilterHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 6px;
`

const FilterTitle = styled.h3`
  color: rgba(0, 0, 0, 0.85);
  font-size: 12px;
  font-weight: 600;
  margin: 0;
  display: flex;
  align-items: center;
  gap: 4px;
  
  svg {
    color: #5c7cfa;
    width: 12px;
    height: 12px;
  }
`

const FilterGrid = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  align-items: flex-end;
  justify-content: space-between;
  
  @media (max-width: 1200px) {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
  }
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`

const LeftFilters = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  align-items: flex-end;
  flex: 1;
`

const RightFilters = styled.div`
  display: flex;
  gap: 10px;
  align-items: flex-end;
  
  @media (max-width: 768px) {
    width: 100%;
    margin-top: 10px;
  }
`

const FilterItem = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  min-width: 150px;
  flex: 0 1 auto;
`

const FilterLabel = styled.label`
  color: rgba(0, 0, 0, 0.65);
  font-size: 10px;
  font-weight: 500;
  text-transform: uppercase;
  letter-spacing: 0.3px;
`

const StyledSelect = styled(Select)`
  .ant-select-selector {
    background: white !important;
    border: 1px solid rgba(0, 0, 0, 0.15) !important;
    border-radius: 6px !important;
    color: rgba(0, 0, 0, 0.85) !important;
    height: 32px !important;
    
    &:hover {
      border-color: rgba(92, 124, 250, 0.4) !important;
    }
  }
  
  &.ant-select-focused .ant-select-selector {
    border-color: rgba(92, 124, 250, 0.6) !important;
    box-shadow: 0 0 0 2px rgba(92, 124, 250, 0.1) !important;
  }
  
  .ant-select-selection-item {
    color: rgba(0, 0, 0, 0.85) !important;
  }
`

const StyledInput = styled(Input)`
  background: white !important;
  border: 1px solid rgba(0, 0, 0, 0.15);
  border-radius: 6px;
  color: rgba(0, 0, 0, 0.85) !important;
  height: 32px;
  
  &::placeholder {
    color: rgba(0, 0, 0, 0.4) !important;
  }
  
  &:hover {
    border-color: rgba(92, 124, 250, 0.4);
  }
  
  &:focus {
    border-color: rgba(92, 124, 250, 0.6);
    box-shadow: 0 0 0 2px rgba(92, 124, 250, 0.1);
  }
  
  input {
    background: transparent !important;
    color: rgba(0, 0, 0, 0.85) !important;
    
    &::placeholder {
      color: rgba(0, 0, 0, 0.4) !important;
    }
  }
`

const SearchWrapper = styled.div`
  position: relative;
  flex: 0 1 250px;
  min-width: 200px;
`

const SearchIcon = styled.div`
  position: absolute;
  left: 12px;
  top: 50%;
  transform: translateY(-50%);
  color: rgba(0, 0, 0, 0.35);
  width: 18px;
  height: 18px;
  pointer-events: none;
  display: flex;
  align-items: center;
  justify-content: center;
  
  svg {
    width: 16px;
    height: 16px;
    stroke-width: 2;
  }
`

const SearchInput = styled(StyledInput)`
  padding-left: 36px;
  
  .ant-input {
    background: transparent !important;
    color: rgba(0, 0, 0, 0.85) !important;
    
    &::placeholder {
      color: rgba(0, 0, 0, 0.4) !important;
    }
  }
  
  .ant-input-clear-icon {
    color: rgba(0, 0, 0, 0.45);
    
    &:hover {
      color: rgba(0, 0, 0, 0.8);
    }
  }
`

const ActionButton = styled(Button)`
  background: white;
  border: 1px solid rgba(92, 124, 250, 0.3);
  color: #5c7cfa;
  border-radius: 6px;
  height: 32px;
  padding: 0 12px;
  display: flex;
  align-items: center;
  gap: 4px;
  font-size: 12px;
  font-weight: 500;
  transition: all 0.2s ease;
  
  &:hover {
    background: rgba(92, 124, 250, 0.05);
    border-color: rgba(92, 124, 250, 0.5);
    color: #5c7cfa;
    transform: translateY(-1px);
  }
  
  &.primary {
    background: linear-gradient(135deg, #5c7cfa 0%, #764ba2 100%);
    border: none;
    color: white;
    
    &:hover {
      background: linear-gradient(135deg, #4c6ef5 0%, #6741a0 100%);
      color: white;
    }
  }
  
  &:active {
    transform: scale(0.98);
  }
`

const FloatingAddButton = styled(Button)`
  position: fixed;
  bottom: 30px;
  right: 30px;
  width: 56px;
  height: 56px;
  border-radius: 50%;
  background: linear-gradient(135deg, #5c7cfa 0%, #764ba2 100%);
  border: none;
  color: white;
  box-shadow: 0 4px 12px rgba(92, 124, 250, 0.4);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  transition: all 0.3s ease;
  
  &:hover {
    background: linear-gradient(135deg, #4c6ef5 0%, #6741a0 100%);
    color: white;
    transform: scale(1.1);
    box-shadow: 0 6px 16px rgba(92, 124, 250, 0.5);
  }
  
  &:active {
    transform: scale(0.95);
  }
  
  svg {
    width: 24px;
    height: 24px;
  }
  
  @media (max-width: 768px) {
    bottom: 20px;
    right: 20px;
    width: 48px;
    height: 48px;
    
    svg {
      width: 20px;
      height: 20px;
    }
  }
`

const SwitchWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 6px 10px;
  background: white;
  border: 1px solid rgba(0, 0, 0, 0.15);
  border-radius: 6px;
  height: 32px;
  
  span {
    color: rgba(0, 0, 0, 0.85);
    font-size: 11px;
    white-space: nowrap;
  }
`

const QuickFilters = styled.div`
  display: flex;
  gap: 6px;
  flex-wrap: wrap;
  margin-top: 10px;
  padding-top: 10px;
  border-top: 1px solid rgba(0, 0, 0, 0.08);
`

const QuickFilterChip = styled.button<{ $active?: boolean }>`
  padding: 4px 10px;
  background: ${props => props.$active 
    ? 'rgba(92, 124, 250, 0.15)' 
    : 'white'};
  border: 1px solid ${props => props.$active 
    ? 'rgba(92, 124, 250, 0.4)' 
    : 'rgba(0, 0, 0, 0.15)'};
  border-radius: 14px;
  color: ${props => props.$active ? '#5c7cfa' : 'rgba(0, 0, 0, 0.65)'};
  font-size: 11px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  gap: 4px;
  
  &:hover {
    background: ${props => props.$active 
      ? 'rgba(92, 124, 250, 0.25)' 
      : 'rgba(255, 255, 255, 0.08)'};
    transform: scale(1.05);
  }
  
  svg {
    width: 14px;
    height: 14px;
  }
`

interface ModernFilterSectionProps {
  title?: string
  showSearch?: boolean
  searchPlaceholder?: string
  onSearch?: (value: string) => void
  onAddClick?: () => void
  onRefresh?: () => void
  onExport?: () => void
  filters?: React.ReactNode
  quickFilters?: Array<{
    label: string
    value: string
    active?: boolean
    onClick?: () => void
  }>
  children?: React.ReactNode
  fullWidth?: boolean
  sortingOptions?: {
    orderField: string
    setOrderField: (value: string) => void
    order: string
    setOrder: (value: string) => void
  }
}

const ModernFilterSection: React.FC<ModernFilterSectionProps> = ({
  title = '필터',
  showSearch = true,
  searchPlaceholder = '검색어를 입력하세요',
  onSearch,
  onAddClick,
  onRefresh,
  onExport,
  filters,
  quickFilters,
  children,
  fullWidth = false,
  sortingOptions
}) => {
  const [searchValue, setSearchValue] = useState('')
  const [isExpanded, setIsExpanded] = useState(true)
  const [searchTimer, setSearchTimer] = useState<NodeJS.Timeout | null>(null)

  const debouncedSearch = useCallback((value: string) => {
    if (searchTimer) {
      clearTimeout(searchTimer)
    }
    
    const timer = setTimeout(() => {
      if (onSearch) {
        onSearch(value)
      }
    }, 1000) // 1초 디바운싱
    
    setSearchTimer(timer)
  }, [onSearch])

  const handleSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value
    setSearchValue(value)
    debouncedSearch(value)
  }
  
  const handleSearchKeyPress = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      // 엔터키를 누르면 즉시 검색
      if (searchTimer) {
        clearTimeout(searchTimer)
      }
      if (onSearch) {
        onSearch(searchValue)
      }
    }
  }
  
  useEffect(() => {
    return () => {
      if (searchTimer) {
        clearTimeout(searchTimer)
      }
    }
  }, [searchTimer])

  return (
    <>
      {onAddClick && fullWidth && (
        <Tooltip title="회원 추가" placement="left">
          <FloatingAddButton onClick={onAddClick}>
            <IconPlus />
          </FloatingAddButton>
        </Tooltip>
      )}
    <FilterContainer $fullWidth={fullWidth}>
      <FilterHeader>
        <FilterTitle>
          <IconFilter size={16} />
          {title}
        </FilterTitle>
        
        <Space size={8}>
          {onAddClick && !fullWidth && (
            <Tooltip title="추가">
              <ActionButton onClick={onAddClick}>
                <IconPlus size={16} />
                추가
              </ActionButton>
            </Tooltip>
          )}
          
          {onRefresh && (
            <Tooltip title="새로고침">
              <ActionButton onClick={onRefresh}>
                <IconRefresh size={16} />
              </ActionButton>
            </Tooltip>
          )}
          
          {onExport && (
            <Tooltip title="내보내기">
              <ActionButton onClick={onExport}>
                <IconDownload size={16} />
              </ActionButton>
            </Tooltip>
          )}
          
          <Tooltip title={isExpanded ? "접기" : "펼치기"}>
            <ActionButton onClick={() => setIsExpanded(!isExpanded)}>
              {isExpanded ? <IconChevronUp size={16} /> : <IconChevronDown size={16} />}
            </ActionButton>
          </Tooltip>
        </Space>
      </FilterHeader>
      
      {isExpanded && (
        <>
          <FilterGrid>
            <LeftFilters>
              {children}
              {showSearch && (
                <SearchWrapper>
                  <SearchIcon>
                    <IconSearch />
                  </SearchIcon>
                  <SearchInput
                    placeholder={searchPlaceholder}
                    value={searchValue}
                    onChange={handleSearch}
                    onKeyPress={handleSearchKeyPress}
                    allowClear
                  />
                </SearchWrapper>
              )}
              {filters}
            </LeftFilters>
            
            {sortingOptions && (
              <RightFilters>
                <FilterItem>
                  <FilterLabel>정렬 기준</FilterLabel>
                  <StyledSelect
                    value={sortingOptions.orderField}
                    onChange={(value) => sortingOptions.setOrderField(value as string)}
                    style={{ width: 120 }}
                    options={[
                      { value: 'id', label: '회원코드' },
                      { value: 'created_at', label: '가입일' },
                      { value: 'lastlogin_at', label: '최근 로그인' },
                    ]}
                  />
                </FilterItem>
                <FilterItem>
                  <FilterLabel>정렬 순서</FilterLabel>
                  <StyledSelect
                    value={sortingOptions.order}
                    onChange={(value) => sortingOptions.setOrder(value as string)}
                    style={{ width: 120 }}
                    options={[
                      { value: 'DESC', label: '내림차순 ↓' },
                      { value: 'ASC', label: '오름차순 ↑' },
                    ]}
                  />
                </FilterItem>
              </RightFilters>
            )}
          </FilterGrid>
          
          {quickFilters && quickFilters.length > 0 && (
            <QuickFilters>
              {quickFilters.map((filter, index) => (
                <QuickFilterChip
                  key={index}
                  $active={filter.active}
                  onClick={filter.onClick}
                >
                  {filter.label}
                  {filter.active && <IconX size={12} />}
                </QuickFilterChip>
              ))}
            </QuickFilters>
          )}
        </>
      )}
    </FilterContainer>
    </>
  )
}

export default React.memo(ModernFilterSection)

// Export styled components for custom filter items
export { 
  FilterItem, 
  FilterLabel, 
  StyledSelect, 
  StyledInput, 
  SwitchWrapper,
  ActionButton,
  FloatingAddButton,
  LeftFilters,
  RightFilters 
}